import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  Upload,
  notification,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";

const MAX_FILE_SIZE_BYTES = 500 * 1024;

const formatBytes = (bytes) => {
  if (!bytes) {
    return "0 B";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  return `${(bytes / 1024).toFixed(1)} KB`;
};

const InvoiceWebBannerSettings = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [removingImageId, setRemovingImageId] = useState(null);
  const [removingMobileImageId, setRemovingMobileImageId] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [desktopFileList, setDesktopFileList] = useState([]);
  const [mobileFileList, setMobileFileList] = useState([]);

  const [cities, setCities] = useState([]);
  const [banners, setBanners] = useState([]);

  const cityNameMap = useMemo(() => {
    const map = {};

    cities.forEach((city) => {
      map[city.id] = city.name;
    });

    return map;
  }, [cities]);

  const getCityLabel = (ids = []) => {
    if (!ids?.length) {
      return t("invoiceWebBanner.allCities");
    }

    const names = ids.map((id) => cityNameMap[id] || `#${id}`);
    return names.join(", ");
  };

  const loadBannerSettings = useCallback(() => {
    setLoading(true);

    coreApi
      .get("/commands/invoice-web-banners")
      .then((response) => {
        const payload = response?.data || {};

        setBanners(payload?.banners || []);
        setCities(payload?.cities || []);
      })
      .catch((error) => {
        notification.error({
          message: error?.response?.data?.message || t("invoiceWebBanner.notifications.loadError"),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    loadBannerSettings();
  }, [loadBannerSettings]);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingBanner(null);
    setDesktopFileList([]);
    setMobileFileList([]);
    form.resetFields();
  };

  const openCreateModal = () => {
    setEditingBanner(null);
    setDesktopFileList([]);
    setMobileFileList([]);
    form.setFieldsValue({
      is_enabled: true,
      link_url: undefined,
      city_branch_ids: [],
    });
    setIsModalOpen(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setDesktopFileList([]);
    setMobileFileList([]);
    form.setFieldsValue({
      is_enabled: !!banner?.is_enabled,
      link_url: banner?.link_url || undefined,
      city_branch_ids: banner?.city_branch_ids || [],
    });
    setIsModalOpen(true);
  };

  const onUploadBefore = (file, setFileList) => {
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      notification.error({
        message: t("invoiceWebBanner.notifications.invalidType"),
      });

      return Upload.LIST_IGNORE;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      notification.error({
        message: t("invoiceWebBanner.notifications.fileTooLarge"),
      });

      return Upload.LIST_IGNORE;
    }

    setFileList([file]);
    return false;
  };

  const handleSave = (values) => {
    if (!editingBanner && desktopFileList.length === 0) {
      notification.error({
        message: t("invoiceWebBanner.notifications.imageRequired"),
      });
      return;
    }

    setSaving(true);

    const formData = new FormData();

    formData.append("is_enabled", values?.is_enabled ? "1" : "0");
    formData.append("link_url", values?.link_url || "");

    (values?.city_branch_ids || []).forEach((id) => {
      formData.append("city_branch_ids[]", String(id));
    });

    if (desktopFileList.length > 0) {
      formData.append("banner", desktopFileList[0]);
    }

    if (mobileFileList.length > 0) {
      formData.append("mobile_banner", mobileFileList[0]);
    }

    const request = editingBanner
      ? (() => {
          formData.append("_method", "PUT");

          return coreApi.post(`/commands/invoice-web-banners/${editingBanner.id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        })()
      : coreApi.post("/commands/invoice-web-banners", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

    request
      .then((response) => {
        notification.success({
          message:
            response?.data?.message
            || (editingBanner
              ? t("invoiceWebBanner.notifications.updateSuccess")
              : t("invoiceWebBanner.notifications.createSuccess")),
        });

        closeModal();
        loadBannerSettings();
      })
      .catch((error) => {
        notification.error({
          message: error?.response?.data?.message || t("invoiceWebBanner.notifications.saveError"),
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleDelete = (id) => {
    setDeletingId(id);

    coreApi
      .delete(`/commands/invoice-web-banners/${id}`)
      .then((response) => {
        notification.success({
          message: response?.data?.message || t("invoiceWebBanner.notifications.deleteSuccess"),
        });

        if (editingBanner?.id === id) {
          closeModal();
        }

        loadBannerSettings();
      })
      .catch((error) => {
        notification.error({
          message: error?.response?.data?.message || t("invoiceWebBanner.notifications.deleteError"),
        });
      })
      .finally(() => {
        setDeletingId(null);
      });
  };

  const handleRemoveImage = (id, type = "desktop", keepModalOpen = false) => {
    const isMobile = type === "mobile";
    const setRemovingState = isMobile ? setRemovingMobileImageId : setRemovingImageId;
    const clearPendingFileList = isMobile ? setMobileFileList : setDesktopFileList;
    const endpoint = isMobile
      ? `/commands/invoice-web-banners/${id}/mobile-image`
      : `/commands/invoice-web-banners/${id}/image`;

    setRemovingState(id);

    coreApi
      .delete(endpoint)
      .then((response) => {
        const updatedBanner = response?.data?.banner || null;

        if (keepModalOpen && updatedBanner) {
          setEditingBanner(updatedBanner);
        }

        clearPendingFileList([]);

        notification.success({
          message:
            response?.data?.message
            || (isMobile
              ? t("invoiceWebBanner.notifications.removeMobileSuccess")
              : t("invoiceWebBanner.notifications.removeSuccess")),
        });

        loadBannerSettings();
      })
      .catch((error) => {
        notification.error({
          message:
            error?.response?.data?.message
            || (isMobile
              ? t("invoiceWebBanner.notifications.removeMobileError")
              : t("invoiceWebBanner.notifications.removeError")),
        });
      })
      .finally(() => {
        setRemovingState(null);
      });
  };

  return (
    <>
      <Card
        title={t("invoiceWebBanner.title")}
        loading={loading}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            {t("invoiceWebBanner.addButton")}
          </Button>
        }
      >
        {banners.length === 0 ? (
          <Empty description={t("invoiceWebBanner.empty")} />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {banners.map((banner) => (
              <Card
                key={banner.id}
                size="small"
                title={`${t("invoiceWebBanner.entry")} #${banner.id}`}
                extra={
                  <Tag color={banner?.is_enabled ? "success" : "default"}>
                    {banner?.is_enabled ? t("invoiceWebBanner.on") : t("invoiceWebBanner.off")}
                  </Tag>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Typography.Text strong>{t("invoiceWebBanner.desktopLabel")}</Typography.Text>
                  {banner?.image_url ? (
                    <img
                      src={banner.image_url}
                      alt={t("invoiceWebBanner.alt")}
                      style={{ width: "100%", maxWidth: 700, height: "auto", borderRadius: 4 }}
                    />
                  ) : (
                    <Typography.Text type="secondary">{t("invoiceWebBanner.noImage")}</Typography.Text>
                  )}

                  <Typography.Text strong>{t("invoiceWebBanner.mobileLabel")}</Typography.Text>
                  {banner?.mobile_image_url ? (
                    <img
                      src={banner.mobile_image_url}
                      alt={t("invoiceWebBanner.mobileAlt")}
                      style={{ width: "100%", maxWidth: 400, height: "auto", borderRadius: 4 }}
                    />
                  ) : (
                    <Typography.Text type="secondary">{t("invoiceWebBanner.noMobileImage")}</Typography.Text>
                  )}

                  <Typography.Text>
                    <strong>{t("invoiceWebBanner.citiesShort")}:</strong> {getCityLabel(banner?.city_branch_ids || [])}
                  </Typography.Text>

                  <Typography.Text>
                    <strong>{t("invoiceWebBanner.linkShort")}:</strong> {banner?.link_url || t("invoiceWebBanner.noLink")}
                  </Typography.Text>

                  <Space wrap>
                    <Button icon={<EditOutlined />} onClick={() => openEditModal(banner)}>
                      {t("invoiceWebBanner.editButton")}
                    </Button>

                    <Button
                      icon={<DeleteOutlined />}
                      loading={removingImageId === banner.id}
                      disabled={!banner?.image_url}
                      onClick={() => handleRemoveImage(banner.id, "desktop")}
                    >
                      {t("invoiceWebBanner.removeImageButton")}
                    </Button>

                    <Button
                      icon={<DeleteOutlined />}
                      loading={removingMobileImageId === banner.id}
                      disabled={!banner?.mobile_image_url}
                      onClick={() => handleRemoveImage(banner.id, "mobile")}
                    >
                      {t("invoiceWebBanner.removeMobileImageButton")}
                    </Button>

                    <Popconfirm
                      title={t("invoiceWebBanner.deleteConfirm")}
                      okText={t("invoiceWebBanner.deleteConfirmOk")}
                      cancelText={t("invoiceWebBanner.deleteConfirmCancel")}
                      onConfirm={() => handleDelete(banner.id)}
                    >
                      <Button danger loading={deletingId === banner.id} icon={<DeleteOutlined />}>
                        {t("invoiceWebBanner.deleteButton")}
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </Card>

      <Modal
        title={editingBanner ? t("invoiceWebBanner.modalEditTitle") : t("invoiceWebBanner.modalCreateTitle")}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okButtonProps={{ loading: saving, icon: <SaveOutlined /> }}
        okText={t("invoiceWebBanner.saveButton")}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item label={t("invoiceWebBanner.enabledLabel")} name="is_enabled" valuePropName="checked">
            <Switch checkedChildren={t("invoiceWebBanner.on")} unCheckedChildren={t("invoiceWebBanner.off")} />
          </Form.Item>

          <Form.Item label={t("invoiceWebBanner.uploadLabel")}>
            <Upload
              beforeUpload={(file) => onUploadBefore(file, setDesktopFileList)}
              fileList={desktopFileList}
              maxCount={1}
              onRemove={() => {
                setDesktopFileList([]);
                return true;
              }}
              accept=".png,.jpg,.jpeg,.webp"
            >
              <Button icon={<UploadOutlined />}>{t("invoiceWebBanner.selectImage")}</Button>
            </Upload>
          </Form.Item>

          <Form.Item label={t("invoiceWebBanner.uploadMobileLabel")}>
            <Upload
              beforeUpload={(file) => onUploadBefore(file, setMobileFileList)}
              fileList={mobileFileList}
              maxCount={1}
              onRemove={() => {
                setMobileFileList([]);
                return true;
              }}
              accept=".png,.jpg,.jpeg,.webp"
            >
              <Button icon={<UploadOutlined />}>{t("invoiceWebBanner.selectMobileImage")}</Button>
            </Upload>
          </Form.Item>

          {editingBanner?.image_url && (
            <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
              <Typography.Text strong>{t("invoiceWebBanner.desktopLabel")}</Typography.Text>
              <img
                src={editingBanner.image_url}
                alt={t("invoiceWebBanner.alt")}
                style={{ width: "100%", maxWidth: 700, height: "auto", borderRadius: 4 }}
              />
              <Space wrap>
                <Tag>{`${editingBanner?.image_width || 0}x${editingBanner?.image_height || 0}px`}</Tag>
                <Tag>{formatBytes(editingBanner?.image_size_bytes)}</Tag>
                <Tag>{editingBanner?.image_mime_type || t("invoiceWebBanner.unknownMime")}</Tag>
              </Space>
              <Button
                icon={<DeleteOutlined />}
                loading={removingImageId === editingBanner.id}
                onClick={() => handleRemoveImage(editingBanner.id, "desktop", true)}
              >
                {t("invoiceWebBanner.removeImageButton")}
              </Button>
            </Space>
          )}

          {editingBanner?.mobile_image_url && (
            <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
              <Typography.Text strong>{t("invoiceWebBanner.mobileLabel")}</Typography.Text>
              <img
                src={editingBanner.mobile_image_url}
                alt={t("invoiceWebBanner.mobileAlt")}
                style={{ width: "100%", maxWidth: 400, height: "auto", borderRadius: 4 }}
              />
              <Space wrap>
                <Tag>{`${editingBanner?.mobile_image_width || 0}x${editingBanner?.mobile_image_height || 0}px`}</Tag>
                <Tag>{formatBytes(editingBanner?.mobile_image_size_bytes)}</Tag>
                <Tag>{editingBanner?.mobile_image_mime_type || t("invoiceWebBanner.unknownMime")}</Tag>
              </Space>
              <Button
                icon={<DeleteOutlined />}
                loading={removingMobileImageId === editingBanner.id}
                onClick={() => handleRemoveImage(editingBanner.id, "mobile", true)}
              >
                {t("invoiceWebBanner.removeMobileImageButton")}
              </Button>
            </Space>
          )}

          <Form.Item
            label={t("invoiceWebBanner.linkLabel")}
            name="link_url"
            rules={[{ type: "url", message: t("invoiceWebBanner.invalidUrl") }]}
          >
            <Input placeholder={t("invoiceWebBanner.linkPlaceholder")} allowClear />
          </Form.Item>

          <Form.Item label={t("invoiceWebBanner.citiesLabel")} name="city_branch_ids">
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder={t("invoiceWebBanner.citiesPlaceholder")}
            >
              {cities.map((city) => (
                <Select.Option key={city.id} value={city.id}>
                  {city.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceWebBannerSettings;
